export type CbsNode = {
  id: string;
  code: string;
  name?: string;
  parent_id?: string | null;
};

export function buildCbsPathMap(nodes: CbsNode[]): Map<string, string> {
  const byId = new Map(nodes.map((n) => [String(n.id), n]));
  const cache = new Map<string, string>();

  const resolve = (id: string): string => {
    const cached = cache.get(id);
    if (cached) return cached;
    const chain: string[] = [];
    let cur: CbsNode | undefined = byId.get(id);
    const seen = new Set<string>();
    while (cur) {
      if (seen.has(String(cur.id))) break;
      seen.add(String(cur.id));
      chain.unshift(String(cur.code));
      const parentId = cur.parent_id ? String(cur.parent_id) : '';
      cur = parentId ? byId.get(parentId) : undefined;
    }
    const path = chain.join(' / ');
    cache.set(id, path);
    return path;
  };

  for (const n of nodes) {
    resolve(String(n.id));
  }
  return cache;
}

export function cbsPathForId(nodes: CbsNode[], cbsCodeId?: string | null): string {
  if (!cbsCodeId) return '—';
  const map = buildCbsPathMap(nodes);
  return map.get(String(cbsCodeId)) || '—';
}

export function cbsPathForCode(
  nodes: CbsNode[],
  code?: string | null,
  cbsByCode?: Map<string, string>
): string {
  if (!code) return '—';
  const id = cbsByCode?.get(code);
  if (id) return cbsPathForId(nodes, id);
  return code;
}

/** Flatten CBS nodes in tree order with depth for indented tables */
export function flattenCbsTree(nodes: CbsNode[]): Array<CbsNode & { depth: number }> {
  const byParent = new Map<string | null, CbsNode[]>();
  for (const n of nodes) {
    const pid = n.parent_id ? String(n.parent_id) : null;
    const list = byParent.get(pid) || [];
    list.push(n);
    byParent.set(pid, list);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => a.code.localeCompare(b.code));
  }
  const out: Array<CbsNode & { depth: number }> = [];
  const walk = (parentId: string | null, depth: number) => {
    for (const n of byParent.get(parentId) || []) {
      out.push({ ...n, depth });
      walk(String(n.id), depth + 1);
    }
  };
  walk(null, 0);
  return out;
}
